/**
 * THE MONTERO — optional add-on (bracelet / bag)
 * assets/montero-addons.js
 *
 * <montero-addon> is a custom element rendered by snippets/montero-addon.liquid.
 *
 * How it works
 *  - Ticking the checkbox adds the REAL Shopify add-on variant to the cart through
 *    /cart/add.js. Unticking removes it through /cart/change.js using the cart
 *    line KEY (never an index or a variant id), so a regular purchase of the same
 *    variant is never touched.
 *  - Every add-on line carries two internal line item properties (underscore
 *    properties are hidden from customers by the theme and by checkout):
 *        _montero_addon_type : bracelet | bag
 *        _montero_addon_for  : id of the product page the add-on was chosen on
 *    A line is "ours" only if BOTH match, so title matching is never used.
 *  - The checkbox always mirrors the real cart: it is synced on load, when the
 *    theme publishes a cart update (drawer remove, etc.), when the tab becomes
 *    visible again and when the page is restored from the back/forward cache.
 *  - Nothing is simulated: no price is calculated here. Totals come from Shopify.
 */
if (!customElements.get('montero-addon')) {
  const PROP_TYPE = '_montero_addon_type';
  const PROP_FOR = '_montero_addon_for';

  const plainText = (html) => new DOMParser().parseFromString(html, 'text/html').body.textContent.trim();

  const routeUrl = (name, fallback) => (window.routes && window.routes[name]) || fallback;

  async function requestJson(url, options) {
    const response = await fetch(url, options);
    let data = {};
    try {
      data = await response.json();
    } catch (e) {
      /* body was not JSON */
    }
    if (!response.ok) {
      const error = new Error(data.description || data.message || 'Request failed');
      error.status = response.status;
      throw error;
    }
    return data;
  }

  const getCart = () =>
    requestJson(routeUrl('cart_url', '/cart') + '.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });

  const postJson = (url, body) =>
    requestJson(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

  async function fetchSection(sectionId) {
    const response = await fetch(`${routeUrl('cart_url', '/cart')}?section_id=${sectionId}`);
    if (!response.ok) throw new Error(`Section ${sectionId} failed`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  class MonteroAddon extends HTMLElement {
    constructor() {
      super();
      this.busy = false;
      this.syncSeq = 0;
      this.messageTimer = null;
      this.committedChecked = false;
      this.committedVariantId = null;
      // One delegated listener per element: nothing can be bound twice.
      this.addEventListener('change', (event) => this.onChange(event));
    }

    connectedCallback() {
      this.committedVariantId = this.currentVariantId();
      this.updatePrice();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.source === 'montero-addon') return;
          this.syncFromCart();
        });
      }
      this.onVisible = () => {
        if (!document.hidden) this.syncFromCart();
      };
      this.onPageShow = (event) => {
        if (!event.persisted) return;
        this.setLoading(false); // back from checkout: clear any "taking you to checkout" state
        this.showMessage('');
        this.syncFromCart();
      };
      // Capture phase: runs before Shopify's own handler on the Buy it now button.
      this.onBuyNowClick = (event) => this.handleBuyNowClick(event);
      document.addEventListener('click', this.onBuyNowClick, true);
      document.addEventListener('visibilitychange', this.onVisible);
      window.addEventListener('pageshow', this.onPageShow);

      this.syncFromCart();
    }

    disconnectedCallback() {
      if (this.unsubscribeCart) this.unsubscribeCart();
      document.removeEventListener('visibilitychange', this.onVisible);
      window.removeEventListener('pageshow', this.onPageShow);
      document.removeEventListener('click', this.onBuyNowClick, true);
      clearTimeout(this.messageTimer);
    }

    /* ---------- element helpers ---------- */

    get input() {
      return this.querySelector('.montero-addon__input');
    }

    get select() {
      return this.querySelector('.montero-addon__select');
    }

    get messageEl() {
      return this.querySelector('.montero-addon__message');
    }

    currentVariantId() {
      const select = this.select;
      return Number(select ? select.value : this.dataset.variantId);
    }

    text(key) {
      return this.dataset[`msg${key.charAt(0).toUpperCase()}${key.slice(1)}`] || '';
    }

    /** Cart lines created by THIS add-on on THIS product page. */
    findLines(cart) {
      return (cart.items || []).filter(
        (item) =>
          item.properties &&
          item.properties[PROP_TYPE] === this.dataset.addonType &&
          String(item.properties[PROP_FOR]) === String(this.dataset.parentId)
      );
    }

    /* ---------- events ---------- */

    onChange(event) {
      // Keep our controls invisible to theme listeners on <product-info>.
      event.stopPropagation();
      const target = event.target;
      if (target.matches('.montero-addon__input')) {
        this.handleToggle(target.checked);
      } else if (target.matches('.montero-addon__select')) {
        this.updatePrice();
        if (this.input && this.input.checked) this.handleVariantChange();
      }
    }

    handleToggle(checked) {
      if (this.busy) {
        // Defensive: controls are disabled while busy, but never trust the DOM.
        this.input.checked = !checked;
        return;
      }
      return this.run(checked ? 'adding' : 'removing', () => (checked ? this.addAddon() : this.removeAddon()));
    }

    handleVariantChange() {
      if (this.busy) {
        this.select.value = String(this.committedVariantId);
        this.updatePrice();
        return;
      }
      // addAddon() adds the new variant first and only then removes the old line.
      return this.run('adding', () => this.addAddon());
    }

    /* ---------- "Buy it now" with an add-on selected ---------- */

    handleBuyNowClick(event) {
      const button = event.target.closest && event.target.closest('.shopify-payment-button__button--unbranded');
      const productInfo = this.closest('product-info');
      if (!button || !productInfo || !productInfo.contains(button)) return;
      // No add-on selected (and nothing pending): leave Shopify's button alone.
      if (!this.committedChecked && !this.busy) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy) return; // an add/remove is still running: never check out half-way
      this.buyNow(productInfo);
    }

    async buyNow(productInfo) {
      const form = productInfo.querySelector('product-form form') || productInfo.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) return;
      this.setLoading(true, 'checkout');
      try {
        // The add-on is already a real cart line; add the main product (same form data
        // the normal Add to cart uses: variant, quantity, selling plan...) then check out the CART.
        await requestJson(routeUrl('cart_add_url', '/cart/add') + '.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        this.goToCheckout();
      } catch (error) {
        this.setLoading(false);
        this.showMessage(error && error.status === 422 && error.message ? error.message : this.text('error'), true);
      }
    }

    goToCheckout() {
      window.location.assign('/checkout');
    }

    /* ---------- cart operations ---------- */

    async addAddon() {
      const cart = await getCart();
      const existing = this.findLines(cart);
      const variantId = this.currentVariantId();

      // Already in the cart with this exact variant: never create a duplicate.
      if (existing.some((line) => line.variant_id === variantId)) return cart;

      await postJson(routeUrl('cart_add_url', '/cart/add') + '.js', {
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              [PROP_TYPE]: this.dataset.addonType,
              [PROP_FOR]: String(this.dataset.parentId),
            },
          },
        ],
      });

      // Variant swap: drop the previously chosen add-on line(s) only after the add succeeded.
      for (const line of existing) {
        await postJson(routeUrl('cart_change_url', '/cart/change') + '.js', { id: line.key, quantity: 0 });
      }
      return getCart();
    }

    async removeAddon() {
      const cart = await getCart();
      const lines = this.findLines(cart);
      if (!lines.length) return cart;
      for (const line of lines) {
        // `id` accepts the line item KEY, which stays stable while other lines change.
        await postJson(routeUrl('cart_change_url', '/cart/change') + '.js', { id: line.key, quantity: 0 });
      }
      return getCart();
    }

    /** Runs one cart action with loading state, error handling and UI refresh. */
    async run(mode, action) {
      this.busy = true;
      this.syncSeq += 1; // invalidates any in-flight background sync
      const focused = this.contains(document.activeElement) ? document.activeElement : null;
      this.setLoading(true, mode);

      try {
        const cart = await action();
        this.applyCartState(cart);
        this.showMessage(this.text(mode === 'removing' ? 'removed' : 'added'));
        await this.refreshCartUi(cart); // never throws
      } catch (error) {
        this.restoreCommittedState();
        this.showMessage(error && error.status === 422 && error.message ? error.message : this.text('error'), true);
      } finally {
        this.setLoading(false);
        if (focused && focused.isConnected) focused.focus({ preventScroll: true });
      }
    }

    async syncFromCart() {
      if (this.busy) return;
      const seq = ++this.syncSeq;
      try {
        const cart = await getCart();
        if (seq !== this.syncSeq || this.busy) return; // stale response
        this.applyCartState(cart);
      } catch (e) {
        /* background sync is best effort */
      }
    }

    /* ---------- UI state ---------- */

    applyCartState(cart) {
      const lines = this.findLines(cart);
      const checked = lines.length > 0;
      const select = this.select;

      if (this.input) this.input.checked = checked;
      if (checked && select) {
        const lineVariant = String(lines[0].variant_id);
        if (Array.from(select.options).some((option) => option.value === lineVariant)) select.value = lineVariant;
      }

      this.committedChecked = checked;
      this.committedVariantId = this.currentVariantId();
      this.updatePrice();
      this.classList.toggle('is-selected', checked);

      const productInfo = this.closest('product-info');
      if (productInfo) productInfo.classList.toggle('montero-addon-selected', checked);
    }

    restoreCommittedState() {
      if (this.input) this.input.checked = this.committedChecked;
      if (this.select && this.committedVariantId) this.select.value = String(this.committedVariantId);
      this.updatePrice();
    }

    updatePrice() {
      const option = this.select && this.select.options[this.select.selectedIndex];
      const priceEl = this.querySelector('.montero-addon__price');
      // The theme's money format can contain HTML (e.g. <span class=money>), so strip it.
      if (option && option.dataset.price && priceEl) priceEl.textContent = plainText(option.dataset.price);
    }

    setLoading(isLoading, mode) {
      this.busy = isLoading;
      this.classList.toggle('is-loading', isLoading);
      this.setAttribute('aria-busy', String(isLoading));
      if (this.input) this.input.disabled = isLoading;
      if (this.select) this.select.disabled = isLoading;
      if (isLoading) this.showMessage(this.text(mode), false, true);
    }

    showMessage(text, isError = false, persist = false) {
      const el = this.messageEl;
      if (!el) return;
      clearTimeout(this.messageTimer);
      el.textContent = text;
      el.dataset.state = isError ? 'error' : 'info';
      if (!isError && !persist) {
        this.messageTimer = setTimeout(() => {
          el.textContent = '';
        }, 4000);
      }
    }

    /* ---------- theme refresh (drawer, header bubble) ---------- */

    async refreshCartUi(cart) {
      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'montero-addon', cartData: cart });
        }
      } catch (e) {
        /* theme listeners must never break the add-on */
      }
      await Promise.all([this.refreshCartIcon(), this.refreshCartDrawer(cart)]);
    }

    async refreshCartIcon() {
      try {
        const bubble = document.getElementById('cart-icon-bubble');
        if (!bubble) return;
        const doc = await fetchSection('cart-icon-bubble');
        const source = doc.querySelector('.shopify-section');
        if (source) bubble.innerHTML = source.innerHTML;
      } catch (e) {
        /* cosmetic */
      }
    }

    async refreshCartDrawer(cart) {
      try {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) return; // cart type is "page" or "notification"
        const doc = await fetchSection('cart-drawer');
        const fresh = doc.querySelector('.drawer__inner');
        const current = drawer.querySelector('.drawer__inner');
        if (fresh && current) current.replaceWith(fresh);
        drawer.classList.toggle('is-empty', cart.item_count === 0);
      } catch (e) {
        /* cosmetic: the next page load shows the correct drawer */
      }
    }
  }

  customElements.define('montero-addon', MonteroAddon);
}
